/**
 * Badge/Status indicator component.
 *
 * @module components/ui/Badge
 */

import { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
  icon?: ReactNode;
}

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400',
  warning: 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400',
  danger: 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
  info: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400',
};

/**
 * Badge component for status indicators
 */
export function Badge({ variant = 'neutral', icon, children, className, ...props }: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {icon}
      {children}
    </span>
  );
}
